import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchSyncJobDetails,
  clearSyncJobState,
} from "store/sync-categories/SyncCategoriesActions";
import {
  selectSyncCategoriesJobDetails,
  selectSyncCategoriesJobId,
} from "store/sync-categories/SyncCategoriesSelectors";

const useSyncJobProgress = () => {
  const dispatch = useDispatch();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const syncJobId = useSelector(selectSyncCategoriesJobId);
  const syncJobDetails = useSelector(selectSyncCategoriesJobDetails);

  const startPolling = useCallback(
    (jobId: string) => {
      // Clear any existing polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Start polling every 5 seconds
      const interval = setInterval(() => {
        dispatch(fetchSyncJobDetails({ syncJobId: jobId }));
      }, 5000);

      pollingIntervalRef.current = interval;
    },
    [dispatch],
  );

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const closeSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    if (pollingIntervalRef.current) {
      stopPolling();
    }
    dispatch(clearSyncJobState());
  }, [stopPolling, dispatch]);

  useEffect(() => {
    if (syncJobId && syncJobId.trim() !== "" && !isSnackbarOpen) {
      setIsSnackbarOpen(true);
      dispatch(fetchSyncJobDetails({ syncJobId }));
    }
  }, [syncJobId, isSnackbarOpen, dispatch]);

  useEffect(() => {
    if (
      syncJobDetails &&
      typeof syncJobDetails === "object" &&
      "status" in syncJobDetails &&
      syncJobDetails.status &&
      syncJobDetails.syncJobId &&
      syncJobDetails.syncJobId.trim() !== ""
    ) {
      if (
        syncJobDetails.status !== "COMPLETED" &&
        syncJobDetails.status !== "FAILED"
      ) {
        startPolling(syncJobDetails.syncJobId);
      } else {
        stopPolling();
      }
    }
  }, [syncJobDetails, startPolling, dispatch, stopPolling]);

  useEffect(() => {
    if (!syncJobId || syncJobId.trim() === "") {
      setIsSnackbarOpen(false);
      stopPolling();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [syncJobId, stopPolling]);

  return {
    syncJobDetails,
    isSnackbarOpen,
    closeSnackbar,
  };
};

export default useSyncJobProgress;
